'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { auditService } from '@repo/supabase/services';
import type { UserProfileData } from '@repo/supabase/services';
import { clientLogger } from '@repo/observability/client';
import { getCurrentUser, getUserProfile } from '../app/actions/auth';

interface UserContextType {
  profile: UserProfileData | null;
  isLoadingProfile: boolean;
  userTimezone: string | null;
  loadProfile: (authId: string) => Promise<void>;
  refetchProfile: () => Promise<void>;
  updateProfile: (profile: UserProfileData | null) => void;
  clearProfile: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * User/Profile Context for Server-Side Auth
 *
 * Philosophy:
 * - No browser auth client usage (server-side auth only)
 * - Profile fetched via server action (getUserProfile) using service_role
 * - Middleware handles auth validation
 * - Components call loadProfile() to load user data
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const loadingRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  // IMPORTANT: Never use browser timezone as fallback - it leads to scheduling errors
  // User must have a timezone set in their profile for booking functionality
  const userTimezone = profile?.timezone || null;

  /**
   * Load profile for authenticated user by authId
   * Uses server action (service_role) — no cookie forwarding issues
   * Prevents concurrent calls via loadingRef
   */
  const loadProfile = useCallback(async (authId: string) => {
    if (!authId) {
      console.log('[UserContext] No authId, cannot load profile');
      return;
    }

    // Skip if already loading or same user already loaded
    if (loadingRef.current) {
      console.log('[UserContext] Already loading, skipping');
      return;
    }

    if (currentUserIdRef.current === authId && profile) {
      console.log('[UserContext] Profile already loaded for user:', authId);
      return;
    }

    loadingRef.current = true;
    setIsLoadingProfile(true);
    try {
      console.log('[UserContext] Loading profile for user:', authId);
      const userProfile = await getUserProfile();

      if (userProfile) {
        console.log('[UserContext] Profile loaded successfully');
        currentUserIdRef.current = authId;
        setProfile(userProfile as UserProfileData);

        // Log successful profile access for audit (non-blocking)
        auditService.log({
          userId: authId,
          action: 'data.access',
          resource: 'user_profile',
          resourceId: authId,
          details: {
            userRole: 'client',
            dataType: 'profile',
            fieldsAccessed: ['firstName', 'lastName', 'email', 'timezone', 'marketingOptIn'],
            timestamp: new Date().toISOString()
          }
        }).catch(err => {
          console.warn('[UserContext] Failed to log profile access audit:', err);
        });
      } else {
        console.log('[UserContext] No profile found for user');
        setProfile(null);
      }
    } catch (error) {
      console.error('[UserContext] Error loading profile:', error);
      await clientLogger.error(error, {
        componentName: 'UserContext',
        action: 'loadProfile',
        metadata: { userId: authId },
        feature: 'auth',
        userImpact: 'blocked'
      });
      setProfile(null);
    } finally {
      setIsLoadingProfile(false);
      loadingRef.current = false;
    }
  }, []); // No dependencies — uses refs to avoid stale closures

  /**
   * Refetch current profile
   */
  const refetchProfile = useCallback(async () => {
    if (currentUserIdRef.current) {
      currentUserIdRef.current = null; // Reset to force reload
      loadingRef.current = false;
      const { user } = await getCurrentUser();
      if (user?.id) {
        await loadProfile(user.id);
      }
    }
  }, [loadProfile]);

  const updateProfile = (newProfile: UserProfileData | null) => {
    console.log('[UserContext] Updating profile');
    setProfile(newProfile);
    if (newProfile?.id) {
      currentUserIdRef.current = newProfile.id;
    }
  };

  const clearProfile = () => {
    console.log('[UserContext] Clearing profile');
    setProfile(null);
    currentUserIdRef.current = null;
  };

  // Auto-load profile on mount if authenticated
  useEffect(() => {
    const init = async () => {
      console.log('[UserContext] Auto-loading profile on mount');
      const { user } = await getCurrentUser();

      if (user?.id) {
        await loadProfile(user.id);
      } else {
        console.log('[UserContext] No user found, skipping profile load');
        setIsLoadingProfile(false);
      }
    };
    init();
  }, [loadProfile]);

  const value: UserContextType = {
    profile,
    isLoadingProfile,
    userTimezone,
    loadProfile,
    refetchProfile,
    updateProfile,
    clearProfile,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
